// 📄 pages/SAP-BTP/components/PerfilesValoresModal.tsx
// Administración de "Perfiles de valores" (docs/plans/plan-perfiles-valores.md,
// P1): datos de un cliente que se repiten al adaptar iFlows (host, alias de
// credencial, Location ID, filtros, rutas de API…). Cada perfil: nombre,
// cliente y ambiente opcionales, y su lista de valores con "Reemplaza a"
// (para aplicarlo de golpe en la P3). Sin contraseñas: esas van a la Vault.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, Lock, Plus, RefreshCw, Save, Search, Trash2, X } from 'lucide-react';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import { olvidarPerfilesEnMemoria } from './SelectorPerfil.tsx';
import { Cliente, listClientesForOrg } from '../../../services/clienteService.ts';
import {
  LIMITES_PERFILES, PerfilValores, ResumenPerfil, TIPOS_VALOR_PERFIL, ValorPerfil, borrarPerfil, crearPerfil, guardarPerfil, listarPerfiles, obtenerPerfil
} from '../../../services/perfilValoresService.ts';

interface PerfilesValoresModalProps {
  orgId: string;
  soloLectura: boolean;
  onCerrar: () => void;
}

type Edicion = { nombre: string; clienteId: string; ambiente: string; valores: ValorPerfil[] };

const vacio = (): Edicion => ({ nombre: '', clienteId: '', ambiente: '', valores: [] });
const deServidor = (p: PerfilValores): Edicion => ({ nombre: p.nombre, clienteId: p.clienteId, ambiente: p.ambiente, valores: p.valores });
const nuevaFila = (): ValorPerfil => ({ id: `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, nombre: '', valor: '', tipo: 'url', reemplaza: '' });

export default function PerfilesValoresModal({ orgId, soloLectura, onCerrar }: PerfilesValoresModalProps) {
  const [perfiles, setPerfiles] = useState<ResumenPerfil[] | null>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  // null = nada abierto; 'nuevo' = perfil sin guardar; id = perfil existente.
  const [abierto, setAbierto] = useState<string | null>(null);
  const [edicion, setEdicion] = useState<Edicion>(vacio());
  const [original, setOriginal] = useState('');
  const [revision, setRevision] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState('');
  const [confirmar, setConfirmar] = useState<null | { tipo: 'descartar'; destino: string | null } | { tipo: 'borrar' }>(null);

  const sucio = abierto !== null && JSON.stringify(edicion) !== original;

  const cargarLista = useCallback(async () => {
    try {
      setPerfiles(await listarPerfiles(orgId));
    } catch (err: any) {
      setError(err?.message || 'No se pudieron cargar los perfiles.');
      setPerfiles([]);
    }
  }, [orgId]);

  useEffect(() => {
    cargarLista();
    // Los clientes son opcionales: si no se pueden leer (permiso), el perfil queda sin cliente.
    listClientesForOrg(orgId).then(setClientes).catch(() => setClientes([]));
  }, [orgId, cargarLista]);

  const abrir = async (destino: string | null) => {
    setError(null);
    setFiltro('');
    if (destino === null) { setAbierto(null); return; }
    if (destino === 'nuevo') {
      const e = { ...vacio(), valores: [nuevaFila()] };
      setAbierto('nuevo');
      setEdicion(e);
      setOriginal(JSON.stringify(vacio()));
      setRevision(0);
      return;
    }
    setAbierto(destino);
    setCargando(true);
    try {
      const p = await obtenerPerfil(orgId, destino);
      const e = deServidor(p);
      setEdicion(e);
      setOriginal(JSON.stringify(e));
      setRevision(p.revision);
    } catch (err: any) {
      setError(err?.message || 'No se pudo abrir el perfil.');
      setAbierto(null);
    } finally {
      setCargando(false);
    }
  };

  // Cambiar de perfil (o cerrar) con cambios sin guardar pide confirmación.
  const irA = (destino: string | null) => (sucio ? setConfirmar({ tipo: 'descartar', destino }) : abrir(destino));
  const cerrar = () => (sucio ? setConfirmar({ tipo: 'descartar', destino: '__cerrar__' }) : onCerrar());

  const guardar = async () => {
    if (!edicion.nombre.trim()) { setError('El perfil necesita un nombre.'); return; }
    // Las filas totalmente vacías no se guardan.
    const valores = edicion.valores.filter((v) => v.nombre.trim() || v.valor || v.reemplaza)
      .map((v) => ({ ...v, nombre: v.nombre.trim(), id: v.id?.startsWith('n') ? undefined : v.id }));
    if (valores.some((v) => !v.nombre)) { setError('Cada valor necesita un nombre (ej. "Host S/4HANA").'); return; }
    setError(null);
    setOcupado(true);
    try {
      const datos = { nombre: edicion.nombre.trim(), clienteId: edicion.clienteId, ambiente: edicion.ambiente.trim(), valores };
      const r = abierto === 'nuevo' ? await crearPerfil(orgId, datos) : await guardarPerfil(orgId, abierto!, datos, revision);
      olvidarPerfilesEnMemoria();
      await cargarLista();
      await abrir(r.id);
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar el perfil.');
    } finally {
      setOcupado(false);
    }
  };

  const borrar = async () => {
    setConfirmar(null);
    if (!abierto || abierto === 'nuevo') return;
    setOcupado(true);
    try {
      await borrarPerfil(orgId, abierto);
      olvidarPerfilesEnMemoria();
      await cargarLista();
      setAbierto(null);
    } catch (err: any) {
      setError(err?.message || 'No se pudo borrar el perfil.');
    } finally {
      setOcupado(false);
    }
  };

  const cambiarFila = (id: string | undefined, cambios: Partial<ValorPerfil>) =>
    setEdicion((e) => ({ ...e, valores: e.valores.map((v) => (v.id === id ? { ...v, ...cambios } : v)) }));
  const quitarFila = (id: string | undefined) => setEdicion((e) => ({ ...e, valores: e.valores.filter((v) => v.id !== id) }));
  const agregarFila = () => setEdicion((e) => ({ ...e, valores: [...e.valores, nuevaFila()] }));

  const filas = useMemo(() => {
    const t = filtro.trim().toLowerCase();
    return t ? edicion.valores.filter((v) => [v.nombre, v.valor, v.reemplaza].some((x) => x.toLowerCase().includes(t))) : edicion.valores;
  }, [edicion.valores, filtro]);

  const editable = !soloLectura;
  const inputCls = 'w-full bg-zinc-800 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 disabled:opacity-60';
  const labelCls = 'block text-[11px] text-gray-400 mb-1';

  return (
    <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4">
      <div className="bg-gray-800 border border-white/10 rounded-2xl w-full max-w-6xl h-[calc(100vh-2rem)] flex flex-col">
        <header className="flex items-start justify-between gap-4 p-5 border-b border-white/10 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2"><KeyRound className="w-5 h-5 text-cyan-400" /> Perfiles de valores</h2>
            <p className="text-xs text-gray-400 mt-1">
              Datos de cada cliente que se repiten al adaptar iFlows: hosts, alias de credencial, Location ID, filtros, rutas de API…
              Se guardan cifrados y solo los ven quien los crea, los owners y los admins de la organización.
            </p>
          </div>
          <button onClick={cerrar} className="text-gray-400 hover:text-white" aria-label="Cerrar"><X className="w-5 h-5" /></button>
        </header>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          {/* Lista de perfiles */}
          <aside className="md:w-64 flex-shrink-0 border-b md:border-b-0 md:border-r border-white/10 p-3 space-y-2 overflow-y-auto max-h-48 md:max-h-none">
            {editable && (
              <button onClick={() => irA('nuevo')} disabled={(perfiles?.length || 0) >= LIMITES_PERFILES.porOrg}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-medium disabled:opacity-40">
                <Plus className="w-3.5 h-3.5" /> Nuevo perfil
              </button>
            )}
            {perfiles === null ? (
              <div className="p-4 text-center text-gray-400"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
            ) : perfiles.length === 0 ? (
              <p className="text-xs text-gray-500 p-2">Todavía no hay perfiles. Crea uno por cliente y ambiente (ej. "ACME – QAS").</p>
            ) : (
              perfiles.map((p) => (
                <button key={p.id} onClick={() => irA(p.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-xs ${abierto === p.id ? 'border-cyan-500/60 bg-cyan-500/10' : 'border-white/10 bg-white/5 hover:border-cyan-500/40'}`}>
                  <p className="text-white font-medium break-words">{p.nombre}</p>
                  <p className="text-gray-500">{[p.clienteId, p.ambiente].filter(Boolean).join(' · ') || 'Sin cliente'} · {p.cantidad} valor{p.cantidad === 1 ? '' : 'es'}</p>
                </button>
              ))
            )}
          </aside>

          {/* Perfil abierto */}
          <section className="flex-1 min-w-0 flex flex-col">
            {error && <div className="m-4 mb-0 bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{error}</div>}
            {abierto === null ? (
              <div className="flex-1 flex items-center justify-center p-8 text-center text-sm text-gray-500">
                Elige un perfil de la lista{editable ? ' o crea uno nuevo' : ''}.
              </div>
            ) : cargando ? (
              <div className="flex-1 flex items-center justify-center text-gray-400"><RefreshCw className="w-5 h-5 animate-spin" /></div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className={labelCls}>Nombre del perfil</label>
                      <input className={inputCls} disabled={!editable} maxLength={120} value={edicion.nombre} placeholder="Ej. ACME – QAS"
                        onChange={(e) => setEdicion((x) => ({ ...x, nombre: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Cliente (opcional)</label>
                      <select className={inputCls} disabled={!editable} value={edicion.clienteId} onChange={(e) => setEdicion((x) => ({ ...x, clienteId: e.target.value }))}>
                        <option value="">Sin cliente</option>
                        {edicion.clienteId && !clientes.some((c) => c.id === edicion.clienteId) && <option value={edicion.clienteId}>{edicion.clienteId}</option>}
                        {clientes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Ambiente (opcional)</label>
                      <input className={inputCls} disabled={!editable} maxLength={40} list="ambientes-perfil" value={edicion.ambiente} placeholder="QAS, PRD…"
                        onChange={(e) => setEdicion((x) => ({ ...x, ambiente: e.target.value }))} />
                      <datalist id="ambientes-perfil"><option value="DEV" /><option value="QAS" /><option value="PRD" /></datalist>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-200 p-2.5 rounded-lg text-[11px]">
                    <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Sin contraseñas:</strong> un iFlow solo lleva el <em>alias</em> de la credencial (la contraseña vive en el Security Material del tenant).
                      Las contraseñas de clientes van en la Vault de credenciales. "Reemplaza a" es opcional: el valor del iFlow original que este cambia al aplicar el perfil.
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-[11px] uppercase tracking-wider text-gray-500">Valores ({edicion.valores.length})</h4>
                    {edicion.valores.length > 5 && (
                      <div className="relative w-56">
                        <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2" />
                        <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Buscar valor…" className={`${inputCls} pl-7`} />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="hidden md:grid grid-cols-[1fr,1.6fr,0.9fr,1.6fr,auto] gap-2 text-[11px] text-gray-500 px-1">
                      <span>Nombre</span><span>Valor</span><span>Tipo</span><span>Reemplaza a (opcional)</span><span className="w-6" />
                    </div>
                    {filas.map((v) => (
                      <div key={v.id} className="grid grid-cols-1 md:grid-cols-[1fr,1.6fr,0.9fr,1.6fr,auto] gap-2 bg-white/5 md:bg-transparent border border-white/10 md:border-0 rounded-lg p-2 md:p-0">
                        <input className={inputCls} disabled={!editable} maxLength={120} value={v.nombre} placeholder="Ej. Host S/4HANA" onChange={(e) => cambiarFila(v.id, { nombre: e.target.value })} />
                        <input className={`${inputCls} font-mono`} disabled={!editable} maxLength={LIMITES_PERFILES.valor} value={v.valor} placeholder="Valor del cliente" spellCheck={false}
                          onChange={(e) => cambiarFila(v.id, { valor: e.target.value })} />
                        <select className={inputCls} disabled={!editable} value={v.tipo} onChange={(e) => cambiarFila(v.id, { tipo: e.target.value as ValorPerfil['tipo'] })}>
                          {TIPOS_VALOR_PERFIL.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <input className={`${inputCls} font-mono`} disabled={!editable} maxLength={LIMITES_PERFILES.valor} value={v.reemplaza} placeholder="Valor del iFlow de origen" spellCheck={false}
                          onChange={(e) => cambiarFila(v.id, { reemplaza: e.target.value })} />
                        {editable ? (
                          <button onClick={() => quitarFila(v.id)} className="p-1.5 text-gray-400 hover:text-red-400 justify-self-end" aria-label="Quitar valor"><Trash2 className="w-4 h-4" /></button>
                        ) : <span />}
                      </div>
                    ))}
                    {filtro && filas.length === 0 && <p className="text-xs text-gray-500">Ningún valor coincide con la búsqueda.</p>}
                    {editable && edicion.valores.length < LIMITES_PERFILES.valores && (
                      <button onClick={agregarFila} className="inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200"><Plus className="w-3.5 h-3.5" /> Agregar valor</button>
                    )}
                  </div>
                </div>

                {editable && (
                  <footer className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-white/10 flex-shrink-0">
                    {abierto !== 'nuevo' ? (
                      <button onClick={() => setConfirmar({ tipo: 'borrar' })} disabled={ocupado}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-red-500/30 text-red-300 hover:bg-red-500/10 disabled:opacity-40">
                        <Trash2 className="w-3.5 h-3.5" /> Borrar perfil
                      </button>
                    ) : <span />}
                    <div className="flex items-center gap-3">
                      <span className={`text-[11px] ${sucio ? 'text-amber-300' : 'text-gray-500'}`}>{sucio ? 'Cambios sin guardar' : 'Guardado'}</span>
                      <button onClick={guardar} disabled={ocupado || !sucio}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-40">
                        {ocupado ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {abierto === 'nuevo' ? 'Crear perfil' : 'Guardar'}
                      </button>
                    </div>
                  </footer>
                )}
              </>
            )}
          </section>
        </div>
      </div>

      {confirmar && (
        <ConfirmDialog
          title={confirmar.tipo === 'borrar' ? 'Borrar perfil' : 'Cambios sin guardar'}
          message={confirmar.tipo === 'borrar' ? `Se borrará "${edicion.nombre}" con sus ${edicion.valores.length} valores. No se puede deshacer.` : 'Tienes cambios sin guardar en este perfil. ¿Descartarlos?'}
          confirmLabel={confirmar.tipo === 'borrar' ? 'Borrar' : 'Descartar'}
          danger
          onConfirm={() => {
            if (confirmar.tipo === 'borrar') { borrar(); return; }
            const destino = confirmar.destino;
            setConfirmar(null);
            if (destino === '__cerrar__') onCerrar();
            else abrir(destino);
          }}
          onCancel={() => setConfirmar(null)}
        />
      )}
    </div>
  );
}
